import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Skeleton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CardMedia,
  Avatar,
  Menu,
  MenuItem,
  IconButton
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { fetchPosts, logout } from '../services/api';
import { useSnackbar } from 'notistack';

function Dashboard() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalAuthors: 0,
    totalTags: 0
  });
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Dashboard - Loading posts data');
        const data = await fetchPosts();
        console.log('Dashboard - Posts loaded:', data);
        setPosts(data);

        // Calculate stats
        const uniqueAuthors = new Set(data.map(post => post.author));
        const allTags = data.flatMap(post => post.tags || []);
        const uniqueTags = new Set(allTags);

        setStats({
          totalPosts: data.length,
          totalAuthors: uniqueAuthors.size,
          totalTags: uniqueTags.size
        });

      } catch (error) {
        console.error('Error loading dashboard data:', error);
        enqueueSnackbar('Failed to load dashboard data', { variant: 'error' });

        // Set empty posts array to prevent errors
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [enqueueSnackbar]);

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    enqueueSnackbar('Logged out successfully', { variant: 'success' });
    navigate('/secure-portal-access');
  };

  const handleSettingsClick = () => {
    handleUserMenuClose();
    navigate('/admin/settings');
  };

  const StatCard = ({ icon, title, value, color }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              width: 60,
              height: 60,
              backgroundColor: `${color}15`,
              color: color,
              mr: 3
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 500 }}>
              {loading ? <Skeleton width={40} /> : value}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Function to get the post thumbnail or a placeholder
  const getPostThumbnail = (post) => {
    if (post.thumbnailData) {
      return post.thumbnailData.data;
    } else {
      // Check if there's image data embedded in content
      return null;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
          WorldWise Scholar Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/posts/create')}
          >
            Create Post
          </Button>
          <IconButton
            color="primary"
            onClick={handleUserMenuOpen}
            aria-label="user menu"
          >
            <AccountCircleIcon />
          </IconButton>
          <Menu
            anchorEl={userMenuAnchorEl}
            open={Boolean(userMenuAnchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleSettingsClick}>
              <SettingsIcon fontSize="small" sx={{ mr: 2 }} />
              Settings
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 2 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<ArticleIcon sx={{ fontSize: 30 }} />}
            title="Total Posts"
            value={stats.totalPosts}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<PersonIcon sx={{ fontSize: 30 }} />}
            title="Total Authors"
            value={stats.totalAuthors}
            color="#f50057"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<LocalOfferIcon sx={{ fontSize: 30 }} />}
            title="Total Tags"
            value={stats.totalTags}
            color="#4caf50"
          />
        </Grid>
      </Grid>

      {/* Featured Posts Section with Images */}
      {!loading && posts.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Featured Posts
          </Typography>
          <Grid container spacing={3}>
            {posts.slice(0, 3).map((post) => (
              <Grid item xs={12} sm={6} md={4} key={post._id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3
                    }
                  }}
                  onClick={() => navigate(`/blog/${post._id}`)}
                >
                  {/* Post Thumbnail */}
                  <Box sx={{ position: 'relative', pt: '56.25%' /* 16:9 Aspect Ratio */ }}>
                    {getPostThumbnail(post) ? (
                      <CardMedia
                        component="img"
                        image={getPostThumbnail(post)}
                        alt={post.title}
                        loading="lazy"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.05)'
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                      </Box>
                    )}
                  </Box>

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom component="h2" noWrap>
                      {post.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {new Date(post.created_at).toLocaleDateString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                        {post.author.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="body2">{post.author}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Recent Posts Table */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Posts
        </Typography>
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          {loading ? (
            <LinearProgress />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {posts.slice(0, 5).map((post) => (
                  <TableRow key={post._id} hover>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getPostThumbnail(post) && (
                          <Avatar
                            variant="rounded"
                            src={getPostThumbnail(post)}
                            alt={post.title}
                            imgProps={{ loading: 'lazy' }}
                            sx={{ mr: 2, width: 36, height: 36 }}
                          />
                        )}
                        {post.title}
                      </Box>
                    </TableCell>
                    <TableCell>{post.author}</TableCell>
                    <TableCell>
                      {new Date(post.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/admin/posts/${post._id}`)}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {posts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No posts found
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        sx={{ mt: 2 }}
                        onClick={() => navigate('/admin/posts/create')}
                      >
                        Create your first post
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        {posts.length > 5 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => navigate('/admin/posts')}
              variant="text"
            >
              View all posts
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Dashboard;